"use client"

import * as React from "react"
import { FaCheck as CheckIcon, FaChevronDown as ChevronDownIcon } from "react-icons/fa"

import { cn } from "@/core/helpers/misc/GeneralUtils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/core/ui/primitives/dropdown-menu"

type SelectValueType = string | undefined

type SelectContextValue = {
  value: SelectValueType
  onValueChange: (value: string) => void
  getLabel: (value: string) => React.ReactNode | undefined
  registerLabel: (value: string, label: React.ReactNode) => void
  unregisterLabel: (value: string) => void
}

const SelectContext = React.createContext<SelectContextValue | null>(null)

function useSelectContext() {
  const context = React.useContext(SelectContext)
  if (!context) {
    throw new Error("Select components must be used within <Select>")
  }
  return context
}

type SelectProps = Omit<React.ComponentProps<typeof DropdownMenu>, "children"> & {
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  children: React.ReactNode
}

function Select({
  value: valueProp,
  defaultValue,
  onValueChange,
  children,
  ...props
}: SelectProps) {
  const [uncontrolledValue, setUncontrolledValue] = React.useState<SelectValueType>(defaultValue)
  const value = valueProp !== undefined ? valueProp : uncontrolledValue

  const [labels, setLabels] = React.useState<Record<string, React.ReactNode>>({})

  const registerLabel = React.useCallback((nextValue: string, label: React.ReactNode) => {
    setLabels((prev) => ({ ...prev, [nextValue]: label }))
  }, [])

  const unregisterLabel = React.useCallback((nextValue: string) => {
    setLabels((prev) => {
      if (!(nextValue in prev)) return prev
      const next = { ...prev }
      delete next[nextValue]
      return next
    })
  }, [])

  const getLabel = React.useCallback((nextValue: string) => labels[nextValue], [labels])

  const handleValueChange = React.useCallback(
    (nextValue: string) => {
      if (valueProp === undefined) {
        setUncontrolledValue(nextValue)
      }
      onValueChange?.(nextValue)
    },
    [onValueChange, valueProp]
  )

  const contextValue = React.useMemo<SelectContextValue>(
    () => ({
      value,
      onValueChange: handleValueChange,
      getLabel,
      registerLabel,
      unregisterLabel,
    }),
    [getLabel, handleValueChange, registerLabel, unregisterLabel, value]
  )

  return (
    <SelectContext.Provider value={contextValue}>
      <DropdownMenu data-slot="select" {...props}>
        {children}
      </DropdownMenu>
    </SelectContext.Provider>
  )
}

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  size?: "sm" | "default"
}) {
  const select = useSelectContext()
  const isPlaceholder = select.value === undefined || select.value === ""

  return (
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        data-slot="select-trigger"
        data-size={size}
        data-placeholder={isPlaceholder ? "" : undefined}
        className={cn(
          "shadcn-selecttrigger shadcn-selectvalue border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          className
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon className="size-4 opacity-50" />
      </button>
    </DropdownMenuTrigger>
  )
}

function SelectContent({
  className,
  children,
  sideOffset = 4,
  align = "start",
  ...props
}: Omit<React.ComponentProps<typeof DropdownMenuContent>, "children"> & {
  children: React.ReactNode
}) {
  return (
    <DropdownMenuContent
      data-select-slot="select-content"
      sideOffset={sideOffset}
      align={align}
      className={cn(
        "shadcn-selectcontent bg-popover text-popover-foreground relative z-50 max-h-[min(320px,var(--radix-dropdown-menu-content-available-height))] min-w-[8rem] overflow-x-hidden overflow-y-auto rounded-md border shadow-md",
        className
      )}
      {...props}
    >
      <div className="shadcn-select p-1">{children}</div>
    </DropdownMenuContent>
  )
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuLabel>) {
  return (
    <DropdownMenuLabel
      data-slot="select-label"
      className={cn("shadcn-selectlabel text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  )
}

function SelectItem({
  className,
  children,
  value,
  onSelect,
  ...props
}: Omit<React.ComponentProps<typeof DropdownMenuItem>, "children"> & {
  children: React.ReactNode
  value: string
}) {
  const select = useSelectContext()

  React.useEffect(() => {
    select.registerLabel(value, children)
    return () => select.unregisterLabel(value)
  }, [children, select, value])

  const isSelected = select.value === value

  return (
    <DropdownMenuItem
      {...props}
      data-slot="select-item"
      className={cn(
        "shadcn-selectitem shadcn-selectlabel focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className
      )}
      onSelect={(event) => {
        onSelect?.(event)
        if (event.defaultPrevented) return
        select.onValueChange(value)
      }}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        {isSelected ? <CheckIcon className="size-4" /> : null}
      </span>
      {children}
    </DropdownMenuItem>
  )
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuSeparator>) {
  return (
    <DropdownMenuSeparator
      data-slot="select-separator"
      className={cn("shadcn-selectseparator bg-border pointer-events-none -mx-1 my-1 h-px", className)}
      {...props}
    />
  )
}

function SelectScrollUpButton() {
  return (
    null
  )
}

function SelectScrollDownButton() {
  return (
    null
  )
}

function SelectGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuGroup>) {
  return <DropdownMenuGroup data-slot="select-group" {...props} />
}

function SelectValue({
  placeholder,
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & { placeholder?: React.ReactNode }) {
  const select = useSelectContext()
  const selectedValue = select.value
  const label = selectedValue ? select.getLabel(selectedValue) : undefined
  const showPlaceholder = !selectedValue || label === undefined

  return (
    <span
      data-slot="select-value"
      data-placeholder={showPlaceholder ? "" : undefined}
      className={cn("flex items-center gap-2", className)}
      {...props}
    >
      {showPlaceholder ? placeholder : label}
    </span>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
